import { type AnyObject } from '@Types';
import { type Cohort } from 'Cohorts';
import { type DateHelper } from 'DateHelper';
import { type Period } from 'Period';
import { isPeriodWithSpecializationStyle } from 'Period/Period.typeguards';
import moment from 'moment';

export function getCurrentPeriodDates(
    cohort: Cohort,
    dateHelper: DateHelper,
): { startDate: Date; endDate: Date } | null {
    if (!cohort || cohort.currentPeriodIndex === null) return null;

    const currentPeriod = cohort.periods[cohort.currentPeriodIndex];

    const rawDates = getRawPeriodDates(cohort, dateHelper);
    const currentPeriodRawDates = rawDates[cohort.currentPeriodIndex];

    if (!currentPeriodRawDates) return null;

    let { startDate, endDate } = currentPeriodRawDates;

    if (isPeriodWithSpecializationStyle(currentPeriod)) {
        const specializationStylePeriodsWithIndex = cohort.periods
            .map((period, index) => ({ period, index }))
            .filter(
                ({ period }) =>
                    isPeriodWithSpecializationStyle(period) &&
                    period.specializationStyle === currentPeriod.specializationStyle,
            );
        startDate = specializationStylePeriodsWithIndex.length
            ? rawDates[specializationStylePeriodsWithIndex[0].index].startDate
            : startDate;
        endDate = specializationStylePeriodsWithIndex.length
            ? rawDates[specializationStylePeriodsWithIndex.at(-1)!.index].endDate
            : endDate;
    }

    return {
        startDate,
        endDate,
    };
}

export function createTitleFromPeriod(
    period: Period,
    formatsText: AnyObject<(text: string) => string>,
    periodIndex: number,
) {
    if (!period) return '';

    const title = formatsText.stripFormatting(period.title);

    if (!isPeriodWithSpecializationStyle(period)) return title || `Week ${periodIndex + 1}`;

    return title.replace(/^(week)\s[0-9]{1,2}:\s/i, match => `${match.trim()} `);
}

export function getRawPeriodDates(cohort: Cohort, dateHelper: DateHelper): { startDate: Date; endDate: Date }[] {
    let rollingDate = moment(cohort.startDate * 1000);
    const dates: { startDate: Date; endDate: Date }[] = [];
    cohort.periods.forEach(period => {
        rollingDate = dateHelper.addInDefaultTimeZone(rollingDate, period.daysOffset, 'days');
        const startDate = rollingDate.toDate();
        rollingDate = dateHelper.addInDefaultTimeZone(rollingDate, period.days, 'days');
        const endDate = rollingDate.toDate();
        dates.push({ startDate, endDate });
    });
    return dates;
}
